import React from 'react';
import { fireEvent, render, screen, waitFor } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import CreateUserButton from './CreateUserButton';

const onCreateUser = jest.fn();

const renderButton = () =>
  render(
    <ConfigProvider>
      <CreateUserButton onCreateUser={onCreateUser} />
    </ConfigProvider>,
  );

beforeEach(() => onCreateUser.mockClear());

const trigger = () => screen.getByRole('button', { name: /create user/i });

describe('management/Users CreateUserButton', () => {
  it('renders the trigger', () => {
    renderButton();

    expect(trigger()).toBeInTheDocument();
  });

  it('starts collapsed', () => {
    renderButton();

    expect(trigger()).toHaveAttribute('aria-expanded', 'false');
    expect(screen.queryByText('Create Multiple Users')).not.toBeInTheDocument();
  });

  describe('when opened', () => {
    it('reports its expanded state', async () => {
      renderButton();

      fireEvent.click(trigger());

      await waitFor(() =>
        expect(trigger()).toHaveAttribute('aria-expanded', 'true'),
      );
    });

    it('offers both creation options', async () => {
      renderButton();

      fireEvent.click(trigger());

      await waitFor(() =>
        expect(screen.getByText('Create Multiple Users')).toBeInTheDocument(),
      );
      expect(screen.getAllByText(/^Create User$/).length).toBeGreaterThan(0);
    });

    it('opens the wizard from the Create User option', async () => {
      renderButton();

      fireEvent.click(trigger());
      await waitFor(() =>
        expect(screen.getByText('Create Multiple Users')).toBeInTheDocument(),
      );

      fireEvent.click(screen.getByRole('option', { name: 'Create User' }));

      expect(onCreateUser).toHaveBeenCalledTimes(1);
    });

    it('closes the menu once an option is chosen', async () => {
      renderButton();

      fireEvent.click(trigger());
      await waitFor(() =>
        expect(screen.getByText('Create Multiple Users')).toBeInTheDocument(),
      );

      fireEvent.click(screen.getByRole('option', { name: 'Create User' }));

      await waitFor(() =>
        expect(
          screen.queryByText('Create Multiple Users'),
        ).not.toBeInTheDocument(),
      );
    });

    // Creating several users at once is still to come, so that option stays inert.
    it('leaves Create Multiple Users disabled', async () => {
      renderButton();

      fireEvent.click(trigger());

      await waitFor(() =>
        expect(screen.getByText('Create Multiple Users')).toBeInTheDocument(),
      );

      expect(
        screen.getByRole('option', { name: 'Create Multiple Users' }),
      ).toHaveAttribute('aria-disabled', 'true');
    });

    it('closes again on a second click', async () => {
      renderButton();

      fireEvent.click(trigger());
      await waitFor(() =>
        expect(screen.getByText('Create Multiple Users')).toBeInTheDocument(),
      );

      fireEvent.click(trigger());

      await waitFor(() =>
        expect(
          screen.queryByText('Create Multiple Users'),
        ).not.toBeInTheDocument(),
      );
    });

    it('closes when the user clicks away', async () => {
      renderButton();

      fireEvent.click(trigger());
      await waitFor(() =>
        expect(screen.getByText('Create Multiple Users')).toBeInTheDocument(),
      );

      fireEvent.mouseDown(document.body);
      fireEvent.click(document.body);

      await waitFor(() =>
        expect(
          screen.queryByText('Create Multiple Users'),
        ).not.toBeInTheDocument(),
      );
    });
  });
});
